import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, ChevronRight, Cable, Package, WifiOff, HardDrive, Globe, Fuel, BookOpen, Wrench, Key, GripVertical, Receipt, MessageSquare, Settings2, Palette, UserMinus, DoorOpen, FolderDown, QrCode, DownloadCloud, ContactRound, Hammer, StickyNote, BellRing, FileText, UserRoundCheck, Building2, Search, X } from 'lucide-react';
import { canAccessSection, type SectionId } from '@shared/types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/auth';

interface MenuItem {
  id: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description?: string;
  section?: SectionId;
}

// Sortable menu items (API keys is always at the bottom, not sortable)
const SORTABLE_ITEMS: MenuItem[] = [
  {
    id: 'company-requisites',
    to: '/more/requisites',
    icon: Building2,
    label: 'Наши реквизиты',
    description: 'Реквизиты юрлиц по датам',
    section: 'settings/requisites',
  },
  {
    id: 'licensing',
    to: '/more/licensing',
    icon: QrCode,
    label: 'Лицензии',
    description: 'Сканирование QR установок ПО',
  },
  {
    id: 'installers',
    to: '/more/installers',
    icon: DownloadCloud,
    label: 'Установщики',
    description: 'Скачивание и доступ к установщикам ПО',
  },
  {
    id: 'door',
    to: '/more/door',
    icon: DoorOpen,
    label: 'Дверь',
    description: 'Открыть дверь в офис',
  },
  {
    id: 'chat',
    to: '/more/chat',
    icon: MessageSquare,
    label: 'AI Чат',
    description: 'AI Ассистент',
  },
  {
    id: 'departed-clients',
    to: '/more/departed-clients',
    icon: UserMinus,
    label: 'Ушедшие клиенты',
    description: 'Клиенты без счетов в текущем периоде',
  },
  {
    id: 'waybills',
    to: '/more/waybills',
    icon: Receipt,
    label: 'Заказ накладных',
    description: 'Товары готовые к выписке накладных',
  },
  {
    id: 'stations-control',
    to: '/more/stations-control',
    icon: HardDrive,
    label: 'Контроль РК',
    description: 'Ошибки резервного копирования станций',
  },
  {
    id: 'isp-status',
    to: '/more/isp-status',
    icon: WifiOff,
    label: 'Статус интернета',
    description: 'Проблемы с провайдерами у клиентов',
  },
  {
    id: 'cable',
    to: '/more/cable',
    icon: Cable,
    label: 'Бухты кабеля',
    description: 'Остатки кабеля и расход',
  },
  {
    id: 'warehouse',
    to: '/more/warehouse',
    icon: Package,
    label: 'Склад',
    description: 'Моё оборудование и офисный склад',
  },
  {
    id: 'route-planner',
    to: '/route-planner',
    icon: Map,
    label: 'Планировщик маршрутов',
    description: 'Построить маршрут между несколькими точками',
  },
  {
    id: 'ip-ez-by',
    to: '/more/ip-ez-by',
    icon: Globe,
    label: 'ip.ez.by',
    description: 'WHOIS, DNS, PTR',
  },
  {
    id: 'fuel',
    to: '/more/fuel',
    icon: Fuel,
    label: 'Учет бензина',
    description: 'Лимит топлива и заправки',
  },
  {
    id: 'action-items',
    to: '/more/action-items',
    icon: BellRing,
    label: 'Требует внимания',
    description: 'Незакрытые действия и сроки',
  },
  {
    id: 'personal-data',
    to: '/more/personal-data',
    icon: UserRoundCheck,
    label: 'Сверка данных',
    description: 'Месячное подтверждение персональных данных',
  },
  {
    id: 'controlled-documents',
    to: '/more/controlled-documents',
    icon: FileText,
    label: 'Документы',
    description: 'Штрихкоды, файлы и сдача документов',
  },
  {
    id: 'kbase',
    to: '/more/kbase',
    icon: BookOpen,
    label: 'База знаний',
    description: 'Проблемы и решения',
  },
  {
    id: 'useful-contacts',
    to: '/more/useful-contacts',
    icon: ContactRound,
    label: 'Полезные контакты',
    description: 'Контакты партнеров и сервисов',
  },
  {
    id: 'personal-notes',
    to: '/more/personal-notes',
    icon: StickyNote,
    label: 'Мои заметки',
    description: 'Личные заметки сотрудника',
  },
  {
    id: 'remont',
    to: '/more/remont',
    icon: Wrench,
    label: 'Ремонт в СЦ',
    description: 'Заявки на ремонт оборудования',
  },
  {
    id: 'subcontractor-works',
    to: '/more/subcontractor-works',
    icon: Hammer,
    label: 'Работы субподрядчиков',
    description: 'Учет работ и документов подрядчиков',
  },
  {
    id: 'file-share',
    to: '/more/file-share',
    icon: FolderDown,
    label: 'Файлы',
    description: 'Раздача файлов по ссылкам',
  },
];

// API keys item - always at the bottom
const API_KEYS_ITEM: MenuItem = {
  id: 'api-keys',
  to: '/profile',
  icon: Key,
  label: 'API ключи',
  description: 'Управление ключами для мобильных приложений',
};

const STORAGE_KEY = 'more-menu-order';
const COLORS_STORAGE_KEY = 'more-menu-colors';

// Predefined colors (dark-theme ready)
const ITEM_COLORS: Record<string, { bg: string; border: string }> = {
  red: { bg: 'bg-red-500/20', border: 'border-red-500/50' },
  orange: { bg: 'bg-orange-500/20', border: 'border-orange-500/50' },
  amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/50' },
  green: { bg: 'bg-green-500/20', border: 'border-green-500/50' },
  cyan: { bg: 'bg-cyan-500/20', border: 'border-cyan-500/50' },
  blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/50' },
  purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/50' },
  pink: { bg: 'bg-pink-500/20', border: 'border-pink-500/50' },
};

function loadSavedOrder(): string[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // Ignore parse errors
  }
  return [];
}

function saveOrder(order: string[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
}

function loadColors(): Record<string, string> {
  try {
    const saved = localStorage.getItem(COLORS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}

function saveColors(colors: Record<string, string>): void {
  localStorage.setItem(COLORS_STORAGE_KEY, JSON.stringify(colors));
}

function sortItemsByOrder(items: MenuItem[], savedOrder: string[]): MenuItem[] {
  // Items not in saved order go to the top (new items)
  const orderMap: Record<string, number> = {};
  savedOrder.forEach((id, index) => {
    orderMap[id] = index;
  });

  return [...items].sort((a, b) => {
    const aIndex = orderMap[a.id];
    const bIndex = orderMap[b.id];

    // New items (not in saved order) go to the top
    if (aIndex === undefined && bIndex === undefined) return 0;
    if (aIndex === undefined) return -1;
    if (bIndex === undefined) return 1;

    return aIndex - bIndex;
  });
}

function matchesSearch(item: MenuItem, query: string): boolean {
  const normalize = (value: string) =>
    value.toLocaleLowerCase('ru-RU').replaceAll('ё', 'е');
  const terms = normalize(query).trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;

  const searchableText = normalize(`${item.label} ${item.description ?? ''}`);
  return terms.every((term) => searchableText.includes(term));
}

// Color picker component
function ColorPicker({ value, onChange }: { value?: string; onChange: (color: string | null) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="p-2 shrink-0 touch-none"
          onClick={(e) => e.stopPropagation()}
        >
          <Palette className={cn(
            'w-5 h-5',
            value ? `text-${value}-500` : 'text-muted-foreground'
          )} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="p-2">
        <div className="grid grid-cols-4 gap-2 mb-2">
          {Object.entries(ITEM_COLORS).map(([key, { bg }]) => (
            <button
              key={key}
              onClick={() => onChange(key)}
              className={cn(
                'w-8 h-8 rounded-full transition-all',
                bg,
                value === key && 'ring-2 ring-offset-2 ring-primary'
              )}
            />
          ))}
        </div>
        {value && (
          <DropdownMenuItem
            onClick={() => onChange(null)}
            className="justify-center text-muted-foreground"
          >
            Убрать цвет
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface SortableItemProps {
  item: MenuItem;
  onNavigate: (to: string) => void;
  isEditMode: boolean;
  color?: string;
  onColorChange: (color: string | null) => void;
}

function SortableItem({ item, onNavigate, isEditMode, color, onColorChange }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const colorStyles = color && ITEM_COLORS[color];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 w-full min-w-0',
        isDragging && 'z-50 opacity-90'
      )}
    >
      {/* Drag handle - only in edit mode */}
      {isEditMode && (
        <button
          {...attributes}
          {...listeners}
          className="p-2 touch-none text-muted-foreground hover:text-foreground shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-5 h-5" />
        </button>
      )}

      {/* Menu item */}
      <button
        onClick={() => onNavigate(item.to)}
        className={cn(
          'flex-1 min-w-0 flex items-center gap-4 p-4 rounded-lg border transition-colors text-left',
          colorStyles
            ? `${colorStyles.bg} ${colorStyles.border} hover:opacity-80`
            : 'bg-card border-border hover:bg-accent'
        )}
      >
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <item.icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium">{item.label}</p>
          {item.description && (
            <p className="text-sm text-muted-foreground">
              {item.description}
            </p>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
      </button>

      {/* Color picker - only in edit mode */}
      {isEditMode && (
        <ColorPicker value={color} onChange={onColorChange} />
      )}
    </div>
  );
}

// Static item (not sortable) - API keys
function StaticItem({ item, onNavigate }: { item: MenuItem; onNavigate: (to: string) => void }) {
  return (
    <button
      onClick={() => onNavigate(item.to)}
      className="w-full flex items-center gap-4 p-4 bg-card rounded-lg border border-border hover:bg-accent transition-colors text-left"
    >
      <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
        <item.icon className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{item.label}</p>
        {item.description && (
          <p className="text-sm text-muted-foreground">
            {item.description}
          </p>
        )}
      </div>
      <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
    </button>
  );
}

export function MorePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isEditMode, setIsEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [itemColors, setItemColors] = useState<Record<string, string>>(() => loadColors());

  const [sortedItems, setSortedItems] = useState<MenuItem[]>(() => {
    const savedOrder = loadSavedOrder();
    return sortItemsByOrder(SORTABLE_ITEMS, savedOrder);
  });

  // Always use the saved order - colors are visual only, don't affect position.
  // Permission-gated entries are also hidden from the sortable list.
  const accessibleItems = useMemo(
    () => sortedItems.filter(
      (item) => !item.section || canAccessSection(user?.roles ?? [], item.section),
    ),
    [sortedItems, user?.roles],
  );
  const displayItems = useMemo(
    () => accessibleItems.filter((item) => matchesSearch(item, searchQuery)),
    [accessibleItems, searchQuery],
  );
  const showApiKeys = matchesSearch(API_KEYS_ITEM, searchQuery);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const itemIds = useMemo(() => displayItems.map((item) => item.id), [displayItems]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSortedItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Save new order to localStorage
        saveOrder(newItems.map((item) => item.id));

        return newItems;
      });
    }
  }, []);

  const handleNavigate = useCallback((to: string) => {
    navigate(to);
  }, [navigate]);

  const handleColorChange = useCallback((itemId: string, color: string | null) => {
    setItemColors(prev => {
      const next = { ...prev };
      if (color) {
        next[itemId] = color;
      } else {
        delete next[itemId];
      }
      saveColors(next);
      return next;
    });
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Header
        title="Ещё"
        rightContent={
          <Button
            variant={isEditMode ? 'default' : 'ghost'}
            size="icon"
            onClick={() => setIsEditMode(prev => !prev)}
          >
            <Settings2 className="h-5 w-5" />
          </Button>
        }
      />

      <main className="p-4 space-y-2 overflow-x-hidden">
        <div className="relative mb-4">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Поиск по разделам"
            aria-label="Поиск по разделам"
            className="pl-9 pr-10"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-2 text-muted-foreground hover:text-foreground"
              aria-label="Очистить поиск"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {displayItems.map((item) => (
              <SortableItem
                key={item.id}
                item={item}
                onNavigate={handleNavigate}
                isEditMode={isEditMode}
                color={itemColors[item.id]}
                onColorChange={(color) => handleColorChange(item.id, color)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {displayItems.length === 0 && !showApiKeys && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Ничего не найдено
          </div>
        )}

        {showApiKeys && (
          <>
            {displayItems.length > 0 && <div className="border-t border-border my-4" />}
            <StaticItem item={API_KEYS_ITEM} onNavigate={handleNavigate} />
          </>
        )}
      </main>
    </div>
  );
}
